import React, { useState, useEffect } from 'react';
import { CCard, CCol, CRow, CFormInput, CButton } from '@coreui/react';
import {
  Utensils,
  Search,
  CheckCircle,
  XCircle,
  Clock,
  Heart,
  PlusCircle,
  RefreshCcw,
} from 'lucide-react';
import './NutritionTracker.scss';

const NutritionTracker = () => {
  // --- State Variables ---
  const [foodItem, setFoodItem] = useState('');
  const [nutritionData, setNutritionData] = useState(null);
  const [servingSize, setServingSize] = useState(null);
  const [healthTip, setHealthTip] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [logStatus, setLogStatus] = useState(null);
  // This state array simulates a FHIR database. In a real app, you would
  // send this data to a a backend service or a real FHIR API.
  const [logHistory, setLogHistory] = useState([]);

  // --- Mock Data & Functions ---
  const mockFoodData = {
    'apple': {
      calories: 95,
      protein: 0.5,
      carbs: 25,
      fat: 0.3,
      servingOptions: ['1 medium apple', '1 large apple', '1 cup sliced'],
      imageUrl: 'https://placehold.co/400x300/a8d8a6/101010?text=Apple',
    },
    'chicken breast': {
      calories: 165,
      protein: 31,
      carbs: 0,
      fat: 3.6,
      servingOptions: ['1 serving (100g)', '1/2 breast', '1 breast (200g)'],
      imageUrl: 'https://placehold.co/400x300/e6e6e6/101010?text=Chicken+Breast',
    },
    'oatmeal': {
      calories: 150,
      protein: 5,
      carbs: 27,
      fat: 3,
      servingOptions: ['1/2 cup dry', '1 cup dry'],
      imageUrl: 'https://placehold.co/400x300/f5f5dc/101010?text=Oatmeal',
    },
    'avocado': {
      calories: 240,
      protein: 3,
      carbs: 13,
      fat: 22,
      servingOptions: ['1/2 avocado', '1 whole avocado'],
      imageUrl: 'https://placehold.co/400x300/b8b800/101010?text=Avocado',
    },
    'salmon': {
      calories: 208,
      protein: 20,
      carbs: 0,
      fat: 13,
      servingOptions: ['1 fillet (100g)'],
      imageUrl: 'https://placehold.co/400x300/ff9999/101010?text=Salmon+Fillet',
    },
  };

  const healthTips = [
    "Remember to stay hydrated throughout the day. Water is essential for every bodily function!",
    "Incorporate a variety of colorful fruits and vegetables into your meals to ensure you get a wide range of vitamins and minerals.",
    "Pay attention to portion sizes. Even healthy foods can contribute to weight gain if consumed in excess.",
    "Try to choose whole grains over refined grains for more fiber, which is great for your digestive health.",
    "Listen to your body's hunger and fullness cues. Mindful eating can help you build a healthier relationship with food.",
  ];

  // Simulated API call to get nutrition data
  const fetchNutritionData = async (food) => {
    setIsFetching(true);
    setNutritionData(null);
    setServingSize(null);
    setFeedbackMessage('');
    setLogStatus(null);
    
    // Simulate a network delay
    await new Promise(resolve => setTimeout(resolve, 1500));

    const lowercaseFood = food.toLowerCase().trim();
    if (mockFoodData[lowercaseFood]) {
      const data = mockFoodData[lowercaseFood];
      setNutritionData({
        calories: data.calories,
        protein: data.protein,
        carbs: data.carbs,
        fat: data.fat,
      });
      setImageUrl(data.imageUrl);
      setFeedbackMessage('Please select the serving size you consumed:');
      setServingSize(null); // Reset serving size for new item
      setHealthTip(healthTips[Math.floor(Math.random() * healthTips.length)]);
    } else {
      setFeedbackMessage(`Sorry, I couldn't find data for "${food}". Please try another food like "apple" or "chicken breast".`);
      setNutritionData(null);
      setImageUrl('');
      setHealthTip('');
    }
    setIsFetching(false);
  };

  // Simulated FHIR database logging
  const logToFhirDatabase = async (data) => {
    setLogStatus('Logging to database...');
    // Simulate a network delay
    await new Promise(resolve => setTimeout(resolve, 2000));

    try {
      // Construct a mock FHIR Observation resource
      const fhirObservation = {
        resourceType: "Observation",
        id: `obs-${Date.now()}`,
        status: "final",
        code: {
          coding: [{
            system: "http://loinc.org",
            code: "37894-0",
            display: "Nutritional Information",
          }],
          text: `Nutrition for ${data.foodItem} (${data.servingSize})`,
        },
        subject: {
          reference: "Patient/patient-123", // Mock patient reference
        },
        effectiveDateTime: new Date().toISOString(),
        component: [
          {
            code: { coding: [{ system: "http://loinc.org", code: "9052-2", display: "Energy" }] },
            valueQuantity: { value: data.nutrition.calories, unit: "kcal", system: "http://unitsofmeasure.org", code: "kcal" },
          },
          {
            code: { coding: [{ system: "http://loinc.org", code: "9050-6", display: "Protein" }] },
            valueQuantity: { value: data.nutrition.protein, unit: "g", system: "http://unitsofmeasure.org", code: "g" },
          },
          {
            code: { coding: [{ system: "http://loinc.org", code: "9049-8", display: "Carbohydrate" }] },
            valueQuantity: { value: data.nutrition.carbs, unit: "g", system: "http://unitsofmeasure.org", code: "g" },
          },
          {
            code: { coding: [{ system: "http://loinc.org", code: "9048-0", display: "Fat" }] },
            valueQuantity: { value: data.nutrition.fat, unit: "g", system: "http://unitsofmeasure.org", code: "g" },
          },
        ],
        // Adding the serving size as an extra comment
        note: [{ text: `Serving size consumed: ${data.servingSize}` }],
      };

      // Add the new observation to the history
      setLogHistory(prevHistory => [...prevHistory, fhirObservation]);
      setLogStatus('Observation successfully logged!');
    } catch (error) {
      setLogStatus('Failed to log observation. Please try again.');
      console.error(error);
    }
  };

  // --- Event Handlers ---
  const handleSearch = (e) => {
    e.preventDefault();
    if (foodItem) {
      fetchNutritionData(foodItem);
    }
  };

  const handleLog = () => {
    if (nutritionData && servingSize) {
      logToFhirDatabase({
        foodItem,
        nutrition: nutritionData,
        servingSize,
      });
    }
  };

  // --- JSX Rendering ---
  return (
    <CCard className="nutrition-tracker">
      <div className="card-header">
        <Utensils size={24} className="card-icon" />
        <h5 className="mb-0">AI Nutrition Tracker</h5>
        <p className="card-subtitle">Log your meals and get instant insights</p>
      </div>
      
      <div className="card-body">
        {/* Input and Search */}
        <form onSubmit={handleSearch} className="search-form">
          <CFormInput
            type="text"
            className="food-input"
            value={foodItem}
            onChange={(e) => setFoodItem(e.target.value)}
            placeholder="Enter a food (e.g., Apple, Chicken Breast)"
            disabled={isFetching}
          />
          <CButton
            type="submit"
            color="success"
            className="search-button"
            disabled={isFetching || !foodItem}
          >
            {isFetching ? (
              <>
                <RefreshCcw className="spinning-icon" size={16} />
                Searching...
              </>
            ) : (
              <>
                <Search size={16} />
                Get Nutrition Facts
              </>
            )}
          </CButton>
        </form>

        {/* Feedback Message */}
        {feedbackMessage && (
          <div className="feedback-message">
            <Clock size={16} />
            <p>{feedbackMessage}</p>
          </div>
        )}

        {/* Nutrition and Image Display */}
        {nutritionData && (
          <div className="nutrition-results">
            <div className="nutrition-display">
              <div className="food-image-container">
                <img
                  src={imageUrl}
                  alt={foodItem}
                  className="food-image"
                  onError={(e) => e.target.src = 'https://placehold.co/400x300/e5e5e5/101010?text=No+Image'}
                />
              </div>
              
              {/* Nutrition Facts */}
              <div className="nutrition-facts">
                <h3 className="food-title">{foodItem}</h3>
                <div className="nutrition-grid">
                  <div className="nutrition-item">
                    <span className="label">Calories:</span>
                    <span className="value">{nutritionData.calories} kcal</span>
                  </div>
                  <div className="nutrition-item">
                    <span className="label">Protein:</span>
                    <span className="value">{nutritionData.protein} g</span>
                  </div>
                  <div className="nutrition-item">
                    <span className="label">Carbs:</span>
                    <span className="value">{nutritionData.carbs} g</span>
                  </div>
                  <div className="nutrition-item">
                    <span className="label">Fat:</span>
                    <span className="value">{nutritionData.fat} g</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Serving Size Selection */}
            <div className="serving-size-section">
              <h4>Select Serving Size:</h4>
              <div className="serving-options">
                {mockFoodData[foodItem.toLowerCase()].servingOptions.map(option => (
                  <button
                    key={option}
                    className={`serving-option ${servingSize === option ? 'selected' : ''}`}
                    onClick={() => setServingSize(option)}
                  >
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Health Tip */}
            <div className="health-tip">
              <Heart size={16} />
              <div>
                <p className="tip-title">Health Tip:</p>
                <p className="tip-content">{healthTip}</p>
              </div>
            </div>

            {/* Log Button and Status */}
            <div className="log-section">
              <CButton
                color="primary"
                className="log-button"
                onClick={handleLog}
                disabled={!servingSize || logStatus === 'Logging to database...'}
              >
                {logStatus === 'Logging to database...' ? (
                  <>
                    <RefreshCcw className="spinning-icon" size={16} />
                    Logging...
                  </>
                ) : (
                  <>
                    <PlusCircle size={16} />
                    Log to FHIR Database
                  </>
                )}
              </CButton>
              
              {logStatus && (
                <div className={`log-status ${logStatus.includes('successfully') ? 'success' : 'error'}`}>
                  {logStatus.includes('successfully') ? (
                    <>
                      <CheckCircle size={16} />
                      {logStatus}
                    </>
                  ) : (
                    <>
                      <XCircle size={16} />
                      {logStatus}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* FHIR Database Log History */}
        {logHistory.length > 0 && (
          <div className="log-history">
            <h3>Logged Observations (Mock FHIR Database)</h3>
            <div className="log-entries">
              {logHistory.slice().reverse().map((log, index) => (
                <div key={log.id} className="log-entry">
                  <pre className="log-content">
                    {JSON.stringify(log, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </CCard>
  );
};

export default NutritionTracker;